import { Array, pipe } from 'effect';

import { alphabet } from '../../../config/alphabet';

import { czechStringOrder } from './czechOrder';

const isDigit = (character: string) => /^\p{Nd}$/u.test(character);

/**
 * Whether two `?alphabet=` values name the same tile.
 *
 * The backend lowercases both sides of its comparison, so casing carries no
 * meaning in this filter — an uppercase deep link selects the same listing as
 * the lowercase grid does, and must light up the same tile.
 */
export const sameAlphabetValue = (a: string, b: string): boolean =>
  a.toLowerCase() === b.toLowerCase();

/**
 * The tiles the alphabet grid renders: the fixed `a–z` / `0–9` grid widened by
 * every initial the alphabet endpoint reports that the grid has no tile for.
 *
 * The catalogue decides what is reachable. The endpoint groups terms by the
 * literal first character of their name and the filter matches on that same
 * character, so a Czech or Slovak initial — `Č`, `Š`, `Ž`, `Ř` — is a listing
 * of its own that no `a–z` tile can reach. Reported initials are carried
 * through **verbatim** rather than re-cased: the endpoint selects them with the
 * same expression the filter compares against, so the reported form is the one
 * guaranteed to match.
 *
 * Extra letters take their place in Czech alphabetical order, beside the letter
 * they accent. Digits stay last, where the fixed grid already puts them.
 */
export const buildGlossaryAlphabetTiles = (
  reportedInitials: readonly string[]
): readonly string[] => {
  const inFixedGrid = new Set(alphabet.map((tile) => tile.toLowerCase()));

  const extras = pipe(
    reportedInitials,
    Array.filter((initial) => initial.length > 0 && !inFixedGrid.has(initial.toLowerCase())),
    Array.dedupeWith(sameAlphabetValue)
  );

  const letters = pipe(
    [...alphabet.filter((tile) => !isDigit(tile)), ...extras.filter((tile) => !isDigit(tile))],
    Array.sortBy(czechStringOrder)
  );

  return [...letters, ...alphabet.filter(isDigit), ...extras.filter(isDigit)];
};
